import { Autocomplete, AutocompleteChangeDetails, Button, Grid, TextField, Tooltip } from '@mui/material';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { resetPortInput, setInputExpand, setSelectedPortId, setSelectedPortIndex, setTriggerRefreshTable, portManagerSelector, setName, setAera, setPorts, setAreas, setMasterArea } from './reducers/PortInputReducer';
import PortSelectorApi, { SearchParam } from '../PortSelector/api/PortSelectorApi';
import PortModel from '../PortSelector/models/PortModel';
import AreaModel from '../PortSelector/models/AreaModel';
import PortSelector from '../PortSelector/PortSelector';
import { portSelectorSelector } from '../PortSelector/reducers/PortSelectorReducer';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';

const PortManagementInput: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const portManagementViewData = useSelector(portManagerSelector);
    let portSelectorData = useSelector(portSelectorSelector);

    useEffect(() => {
        if (portManagementViewData.triggerRefreshInput > 0) {
            refreshInput();
        }
    }, [portManagementViewData.triggerRefreshInput]);

    const refreshInput = () => {
        dispatch(resetPortInput());
        dispatch(setSelectedPortId(-1));
        dispatch(setSelectedPortIndex(-1));
    }

    useEffect(() => {
        dispatch(showLoading(true));

        PortSelectorApi.Search({ searchText: portManagementViewData.searchText } as SearchParam)
            .then((response: BaseResponse<PortModel[]>) => {
                if (response.success) {
                    let areas: string[] = [];

                    _.each(response.data, (port) => {
                        let selectedArea = _.find(areas, (area: AreaModel) => {
                            return area.name === port.area;
                        });

                        if (!selectedArea) {
                            areas.push(port.area);
                        }

                        port.positive = true;
                    });

                    dispatch(setPorts(response.data));
                    dispatch(setAreas(areas));
                } else {
                    Utility.Alert.Error(response.message);
                }
            })
            .catch((error) => {
                Utility.Alert.Error(error);
            }).finally(() => {
                dispatch(showLoading(false));
            })
    }, [portManagementViewData.triggerRefreshTable]);

    const save = () => {
        try {
            validate();

            PortSelectorApi.Save(portManagementViewData.input)
                .then((response: BaseResponse<string>) => {
                    if (response.success) {
                        if (portManagementViewData.input.id == -1) {
                            Utility.Alert.Success("Port added successful");
                        } else {
                            Utility.Alert.Success("Port updated successful");
                        }

                        refreshInput();

                        dispatch(setTriggerRefreshTable(new Date().getTime()));
                    }
                    else {
                        Utility.Alert.Error(response.message);
                    }
                })
                .catch((error) => {
                    Utility.Alert.Error(error);
                });
        } catch (error) {
            Utility.Alert.Error(error);
        }
    }

    const validate = () => {
        if (portManagementViewData.input.name.length === 0) {
            throw "Name is required";
        }

        if (portManagementViewData.input.area.length === 0) {
            throw "Area is required";
        }
    }

    return (
        <>
            <CustomAccordion
                title="Input"
                defaultOpen={true}
                className="top-quick-input"
                open={portManagementViewData.inputExpand}
                onOpenChange={(isOpen: boolean) => {
                    dispatch(setInputExpand(isOpen));
                }}
            >
                <Grid container>
                    <Grid item lg={4} xs={12}
                        className='cargo-input-component'
                    >
                        <Grid container>
                            <Grid xs={10} lg={9}>
                                <TextField
                                    label="Name"
                                    fullWidth
                                    value={portManagementViewData.input.name}
                                    onChange={(event) => {
                                        dispatch(setName(event.target.value.toUpperCase()));
                                    }}
                                    className="tonnage-input-full-tc"
                                />
                            </Grid>
                            <Grid item xs={2} lg={3}>
                                <Tooltip title="Reset">
                                    <Button
                                        className="clear-input-button"
                                        color="success"
                                        variant="contained"
                                        tabIndex={-1}
                                        onClick={() => {
                                            refreshInput();
                                        }}
                                    >
                                        <i className="fa fa-refresh" aria-hidden="true"></i>
                                    </Button>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item lg={3} xs={12}
                        className="cargo-input-component"
                    >
                        <PortSelector
                            onAreaChange={(value: AreaModel | string | null) => {
                                if (value) {
                                    let areaName = value as AreaModel ? (value as AreaModel).name : (value as string);
                                    dispatch(setAera(areaName));
                                } else {
                                    dispatch(setAera(""));
                                }
                            }}
                            areaValue={{
                                name: portManagementViewData.input.area,
                                positive: true
                            } as AreaModel}
                            areaLabel='Area'
                            isAreaDisabled={false}
                            hidePort={true}
                            hideArea={false}
                            freeSolo={true}
                            onAreaInputChange={(value: string) => {
                                dispatch(setAera(value));
                            }}
                        >
                        </PortSelector>
                    </Grid>
                    <Grid item lg={3} xs={12}
                        className="cargo-input-component"
                    >
                        <Autocomplete
                            className='cargo-input-component'
                            options={portSelectorData.masterAreas}
                            renderInput={(params) => <TextField {...params} label="Master Area" />}
                            value={portManagementViewData.input.masterArea}
                            onChange={(event: React.SyntheticEvent, value: string | null, reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                                dispatch(setMasterArea(value ? value : ""));
                            }}
                        />
                    </Grid>
                    <Grid item lg={2} xs={12}
                    >
                        <Button
                            onClick={save}
                            variant="outlined"
                        >
                            Save
                        </Button>
                    </Grid>
                </Grid>
            </CustomAccordion>
        </>
    )
};

export default PortManagementInput;